/**
 * Zoom calculation utilities for PDF viewer
 */

import type { PDFZoomMode } from '../types'

export interface ContainerDimensions {
  width: number
  height: number
}

export interface PageDimensions {
  width: number
  height: number
}

// Default zoom constraints
export const MIN_ZOOM = 25
export const MAX_ZOOM = 500
export const ZOOM_STEP = 25

// Preset zoom levels for dropdown
export const ZOOM_PRESETS = [50, 75, 100, 125, 150, 200, 300, 400]

/**
 * Calculates the zoom level to fit page width within container
 */
export function calculateFitWidthZoom(
  container: ContainerDimensions,
  page: PageDimensions,
  padding = 40,
): number {
  const availableWidth = container.width - padding
  const zoom = (availableWidth / page.width) * 100
  return clampZoom(zoom)
}

/**
 * Calculates the zoom level to fit entire page within container
 */
export function calculateFitPageZoom(
  container: ContainerDimensions,
  page: PageDimensions,
  padding = 40,
): number {
  const availableWidth = container.width - padding
  const availableHeight = container.height - padding

  const widthZoom = (availableWidth / page.width) * 100
  const heightZoom = (availableHeight / page.height) * 100

  return clampZoom(Math.min(widthZoom, heightZoom))
}

/**
 * Calculates the zoom level to fit two pages side-by-side within container
 */
export function calculateTwoPageZoom(
  container: ContainerDimensions,
  page: PageDimensions,
  gap = 32,
  padding = 40,
): number {
  const availableWidth = container.width - padding
  const availableHeight = container.height - padding

  const totalPageWidth = (page.width * 2) + gap
  const widthZoom = (availableWidth / totalPageWidth) * 100
  const heightZoom = (availableHeight / page.height) * 100

  return clampZoom(Math.min(widthZoom, heightZoom) * 0.95)
}

/**
 * Calculates automatic zoom based on page dimensions and container.
 * Uses fit-width for portrait pages, fit-page for landscape.
 */
export function calculateAutoZoom(
  container: ContainerDimensions,
  page: PageDimensions,
  padding = 40,
): number {
  const pageAspect = page.width / page.height
  const containerAspect = container.width / container.height

  // For pages wider than container aspect, use fit-width
  // For taller pages, use fit-page
  if (pageAspect > containerAspect) {
    return calculateFitWidthZoom(container, page, padding)
  }
  return calculateFitPageZoom(container, page, padding)
}

/**
 * Resolves a zoom mode or number to an actual zoom percentage
 */
export function resolveZoom(
  zoom: number | PDFZoomMode,
  container: ContainerDimensions,
  page: PageDimensions,
  padding = 40,
): number {
  if (typeof zoom === 'number') {
    return clampZoom(zoom)
  }

  switch (zoom) {
    case 'fit-width':
      return calculateFitWidthZoom(container, page, padding)
    case 'fit-page':
      return calculateFitPageZoom(container, page, padding)
    case 'auto':
      return calculateAutoZoom(container, page, padding)
  }
}

/**
 * Clamps zoom level to valid range
 */
export function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(zoom)))
}

/**
 * Increments zoom by step, optionally snapping to presets
 */
export function incrementZoom(currentZoom: number, step = ZOOM_STEP): number {
  return clampZoom(currentZoom + step)
}

/**
 * Decrements zoom by step, optionally snapping to presets
 */
export function decrementZoom(currentZoom: number, step = ZOOM_STEP): number {
  return clampZoom(currentZoom - step)
}

/**
 * Converts zoom percentage to scale factor (for canvas rendering)
 */
export function zoomToScale(zoom: number): number {
  return zoom / 100
}

/**
 * Converts scale factor to zoom percentage
 */
export function scaleToZoom(scale: number): number {
  return Math.round(scale * 100)
}

/**
 * Formats zoom for display (e.g., "100%")
 */
export function formatZoom(zoom: number): string {
  return `${Math.round(zoom)}%`
}
